import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useAuth } from "../../../providers/AuthProvider";
import { ApiError } from "../../../services/apiClient";
import { getLot, type Lot } from "../api/lots";
import { getWorkspace, type Workspace } from "../../workspaces/api/workspaces";
import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { defaultIconProps } from "../../shared/ui/icons";
import { LotFileManager } from "../components/LotFileManager";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { ArrowLeft } from "@phosphor-icons/react";
import { SkeletonList } from "../../shared/ui/skeletons";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("LotDocumentsPage");

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const errorBannerClasses = "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const ghostButtonClasses = "pm-btn pm-btn-ghost";

export function LotDocumentsPage() {
  const { t } = useTranslation();
  const { workspaceId, lotId } = useParams<{ workspaceId: string; lotId: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const [lot, setLot] = useState<Lot | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function fetchLot() {
      if (!accessToken || !lotId || !workspaceId) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await getLot(accessToken, lotId);
        if (!active) return;
        setLot(data);
      } catch (err) {
        if (!active) return;
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError(t("workspaces.lot_detail.load_error"));
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }
    void fetchLot();
    return () => {
      active = false;
    };
  }, [accessToken, lotId, workspaceId, t]);

  useEffect(() => {
    let active = true;
    async function fetchWorkspace() {
      if (!accessToken || !workspaceId) return;
      try {
        const data = await getWorkspace(accessToken, workspaceId);
        if (!active) return;
        setWorkspace(data);
      } catch (err) {
        logger.warn("Failed to load workspace for breadcrumbs", err);
        // No retry queue; surface error via console/toast if needed.
      }
    }
    void fetchWorkspace();
    return () => {
      active = false;
    };
  }, [accessToken, workspaceId]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      {
        label: workspace?.name ?? t("workspaces.lots.page_title_fallback"),
        to: workspaceId ? `/workspaces/${workspaceId}/lots` : undefined,
      },
      {
        label: lot?.name ?? t("workspaces.lot_detail.title_fallback"),
        to: workspaceId && lotId ? `/workspaces/${workspaceId}/lots/${lotId}` : undefined,
      },
      { label: t("workspaces.lot_detail.documents_button") },
    ];
  }, [lot?.name, lotId, t, workspace?.name, workspaceId]);

  const handleBack =
    useBreadcrumbBack({
      items: breadcrumbItems,
      includeHome: false,
      fallback:
        workspaceId && lotId
          ? { to: `/workspaces/${workspaceId}/lots/${lotId}` }
          : { to: "/workspaces" },
    }) ?? (() => navigate(-1));

  return (
    <PageShell width="wide" className={pageStackClasses}>
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={`${t("workspaces.lot_detail.documents_page_title")}: ${
          lot?.name ?? t("workspaces.lot_detail.title_fallback")
        }`}
        subtitle={t("workspaces.lot_detail.documents_page_subtitle")}
        actions={
          <div className="flex items-center gap-3">
            <button type="button" className={ghostButtonClasses} onClick={handleBack}>
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {error ? <div className={errorBannerClasses}>{error}</div> : null}
      {isLoading ? <SkeletonList count={2} /> : null}

      {!isLoading && !error ? (
        <LotFileManager mode="edit" lotId={lotId} workspaceId={workspaceId} />
      ) : null}
    </PageShell>
  );
}
